import { useCallback, useEffect, useRef, useState } from 'react';
import {
  beginAuthorize,
  clearSession,
  completeAuthorize,
  loadSession,
  refreshSession,
  RefreshTokenInvalidError,
  type ZoomSession,
} from '../lib/auth';
import { initZoom, zoomSdk } from '../lib/zoom';
import { setMediatorTokenRefresher } from '../api/mediatorService';

type State =
  | { status: 'idle'; session: ZoomSession | null }
  | { status: 'exchanging' }
  | { status: 'error'; error: string };

export interface AuthApi {
  state: State;
  signIn: () => Promise<void>;
  signOut: () => void;
}

// Refresh this many milliseconds before the access token actually expires, so
// the sidebar never hands out a token that's within 60s of expiry.
const REFRESH_LEAD_MS = 60_000;

/**
 * Provides the mediator's Zoom OAuth session and drives:
 *   - Initial sign-in handshake (via SDK event or URL redirect)
 *   - Silent refresh of the access token before expiry, and on mount when
 *     the stored session is close to / past expiry.
 *   - Clearing the session and dropping to the sign-in prompt when Zoom
 *     rejects the refresh token.
 */
export function useAuth(): AuthApi {
  const [state, setState] = useState<State>(() => ({
    status: 'idle',
    session: loadSession(),
  }));
  const refreshTimer = useRef<number | null>(null);
  // Current session, kept in a ref so the on-demand token refresher (used by
  // mediatorService on a 401) always sees the latest without re-registering.
  const sessionRef = useRef<ZoomSession | null>(state.status === 'idle' ? state.session : null);
  sessionRef.current = state.status === 'idle' ? state.session : sessionRef.current;
  // Guards against double-exchange: Zoom's in-client OAuth delivers the same
  // authorization code via both `onAuthorized` event and `?code=&state=` URL
  // params. Whichever fires first wins; the other is a no-op.
  const exchangeGuard = useRef(false);

  const scheduleRefresh = useCallback((session: ZoomSession) => {
    if (refreshTimer.current !== null) {
      window.clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
    const msUntilRefresh = session.expiresAt - Date.now() - REFRESH_LEAD_MS;
    if (msUntilRefresh <= 0) {
      // Already past the lead window — refresh immediately.
      void runRefresh(session);
      return;
    }
    refreshTimer.current = window.setTimeout(() => {
      void runRefresh(session);
    }, msUntilRefresh);
  }, []);

  const runRefresh = useCallback(
    async (session: ZoomSession) => {
      try {
        const next = await refreshSession(session);
        setState({ status: 'idle', session: next });
        scheduleRefresh(next);
      } catch (err) {
        if (err instanceof RefreshTokenInvalidError) {
          setState({ status: 'idle', session: null });
          return;
        }
        // Network hiccups shouldn't kick the user out — keep the current
        // session, try again on the next scheduled tick.
        console.error('[auth] refresh failed', err);
        scheduleRefresh(session);
      }
    },
    [scheduleRefresh],
  );

  // Register the on-demand refresher mediatorService calls when a request
  // 401s (expired Zoom token). Returns a fresh access token, or null when the
  // refresh token is dead (→ drop to sign-in) or a network error (→ keep the
  // session, let the caller surface the 401).
  useEffect(() => {
    setMediatorTokenRefresher(async () => {
      const current = sessionRef.current;
      if (!current) return null;
      try {
        const next = await refreshSession(current);
        setState({ status: 'idle', session: next });
        scheduleRefresh(next);
        return next.accessToken;
      } catch (err) {
        if (err instanceof RefreshTokenInvalidError) {
          setState({ status: 'idle', session: null });
        }
        return null;
      }
    });
    return () => setMediatorTokenRefresher(null);
  }, [scheduleRefresh]);

  // On mount: if we loaded a session, make sure it's still valid.
  useEffect(() => {
    const session = state.status === 'idle' ? state.session : null;
    if (!session) return;
    if (session.expiresAt - Date.now() <= REFRESH_LEAD_MS) {
      void runRefresh(session);
    } else {
      scheduleRefresh(session);
    }
    return () => {
      if (refreshTimer.current !== null) {
        window.clearTimeout(refreshTimer.current);
        refreshTimer.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL-redirect fallback (running outside of Zoom client).
  useEffect(() => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const callbackState = url.searchParams.get('state');
    if (!code || !callbackState) return;
    // Always clear the URL so a reload doesn't re-attempt the (consumed) code.
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    window.history.replaceState({}, '', url.toString());

    if (exchangeGuard.current) return;
    exchangeGuard.current = true;

    let cancelled = false;
    setState({ status: 'exchanging' });

    completeAuthorize({ code, state: callbackState })
      .then((session) => {
        if (cancelled) return;
        setState({ status: 'idle', session });
        scheduleRefresh(session);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        exchangeGuard.current = false;
        setState({
          status: 'error',
          error: err instanceof Error ? err.message : 'Authorization failed',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [scheduleRefresh]);

  // In-Client OAuth: register the SDK event handler once the SDK config is done.
  useEffect(() => {
    let cancelled = false;

    initZoom()
      .then(() => {
        if (cancelled) return;
        zoomSdk.onAuthorized((event) => {
          if (cancelled) return;
          console.log('[auth] onAuthorized event', {
            result: event.result,
            hasCode: Boolean(event.code),
            hasState: Boolean(event.state),
            redirectUri: event.redirectUri,
            codePrefix: event.code?.slice(0, 6),
          });
          if (!event.result) {
            setState({ status: 'error', error: 'Zoom authorization was declined' });
            return;
          }
          if (!event.code) {
            setState({
              status: 'error',
              error: 'Zoom did not return an authorization code',
            });
            return;
          }
          if (exchangeGuard.current) return;
          exchangeGuard.current = true;

          setState({ status: 'exchanging' });
          completeAuthorize({
            code: event.code,
            state: event.state ?? null,
            redirectUri: event.redirectUri ?? null,
          })
            .then((session) => {
              if (cancelled) return;
              setState({ status: 'idle', session });
              scheduleRefresh(session);
            })
            .catch((err: unknown) => {
              if (cancelled) return;
              exchangeGuard.current = false;
              setState({
                status: 'error',
                error: err instanceof Error ? err.message : 'Authorization failed',
              });
            });
        });
      })
      .catch(() => {
        // SDK unavailable (e.g. plain browser) — URL-redirect path will handle it.
      });

    return () => {
      cancelled = true;
    };
  }, [scheduleRefresh]);

  const signInPending = useRef(false);
  const signIn = useCallback(async () => {
    if (signInPending.current) return; // Ignore rapid double-clicks.
    signInPending.current = true;
    setState({ status: 'exchanging' });
    try {
      await beginAuthorize();
    } catch (err) {
      signInPending.current = false;
      setState({
        status: 'error',
        error: err instanceof Error ? err.message : 'Failed to start sign-in',
      });
      return;
    }
    // Leave signInPending=true until the callback runs (success or failure);
    // the pending guard clears on auth-state change below.
  }, []);

  useEffect(() => {
    if (state.status !== 'exchanging') {
      signInPending.current = false;
    }
  }, [state.status]);

  const signOut = useCallback(() => {
    if (refreshTimer.current !== null) {
      window.clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
    clearSession();
    setState({ status: 'idle', session: null });
  }, []);

  return { state, signIn, signOut };
}
